import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import * as UserActions from './user.actions';
import { Store } from '@ngrx/store';
import {
  catchError,
  concatMap,
  map,
  exhaustMap,
  withLatestFrom,
  filter,
} from 'rxjs/operators';
import { combineLatest, of } from 'rxjs';
import { globalFailure } from './../../../app/@store/common.actions';
import {
  getUserListConfig,
  getUsersMeta,
  getUsersTotal,
} from './../../../app/@store/user/user.selectors';
import { User } from './../../../app/@shared/models/user.model';
import { IMeta } from './../../../app/@store/request-options.interface';
import { PublicPartnerApiService } from './../../../app/@shared/services/publicpartnerapi.service';
import { getAuth } from '../auth/auth.selectors';
import { HttpClient, HttpParams } from '@angular/common/http';
import { UsersResponse } from './../../../app/@shared/models/users_response.model';

import * as AuthActions from '../auth/auth.actions';
import * as _ from 'lodash';

@Injectable()
export class UserEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly store: Store,
    private readonly api: PublicPartnerApiService,
    private readonly http: HttpClient
  ) {}

  loadUsersTotal$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.loadUsersTotal, AuthActions.loginSuccess),
      concatMap((action) => {
        return this.api
          .get<Partial<UsersResponse>>('/users', {
            fields: 'object_uuid',
            limit: '1',
            page: '0',
          })
          .pipe(
            filter((result) => result?.users !== undefined),
            map((result) => {
              const total = result.users![0]?.total_row_count || 0;

              return UserActions.loadUsersTotalSuccess({
                total,
              });
            }),
            catchError((error) =>
              of(
                UserActions.loadUsersTotalFailure({ error }),
                globalFailure({ error })
              )
            )
          );
      })
    )
  );

  loadUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.loadUsers, UserActions.userTableChangeSort),
      filter((action) => action.fromBatch !== true),
      withLatestFrom(this.store.select(getUserListConfig), this.store.select(getUsersTotal)),
      concatMap(([action, config, metaTotal]) =>
        this.api.get<Partial<UsersResponse>>('/users', { ..._.omitBy(config, c => c === undefined) }).pipe(
          filter((result) => result?.users !== undefined),
          map((result) => {
            const total = result.users![0]?.total_row_count || metaTotal || 0;

            const meta: IMeta = {
              count: result.users!.length,
              limit: config?.limit || 10,
              page: 0,
              total,
            };

            return UserActions.loadUsersSuccess({
              data: result.users || [],
              meta,
            });
          }),
          catchError((error) =>
            of(
              UserActions.loadUsersFailure({ error }),
              globalFailure({ error })
            )
          )
        )
      )
    )
  );

  loadUsersAutocomplete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.loadUsersAutocomplete),
      concatMap((action) =>
        this.api
          .get<Partial<UsersResponse>>(
            '/users',
            new HttpParams({
              fromObject: {
                fields: 'first_name,last_name,email,locked',
                ...action.config,
              },
            })
          )
          .pipe(
            map((result) => {
              return UserActions.loadUsersAutocompleteSuccess({
                data: result.users || [],
              });
            }),
            catchError((error) =>
              of(
                UserActions.loadUsersAutocompleteFailure({ error }),
                globalFailure({ error })
              )
            )
          )
      )
    )
  );

  loadUsersNext$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.loadUsersNext),
      withLatestFrom(
        combineLatest([
          this.store.select(getUsersMeta),
          this.store.select(getUserListConfig),
        ])
      ),
      exhaustMap(([action, [_meta, _listConfig]]) => {
        const nextPage = (_meta?.page || 0) + 1;
        const totalPages = Math.ceil(
          (_meta?.total || 0) / (_meta?.limit || 10)
        );

        const config = { ..._.omitBy(_listConfig, c => c === undefined) };

        if (nextPage <= totalPages) {
          config.page = nextPage;
        }

        return this.api
          .get<Partial<UsersResponse>>('/users', config as HttpParams)
          .pipe(
            filter((result) => result.users !== undefined),
            map((result) => {
              const total = result.users![0]?.total_row_count || _meta.total || 0;

              const meta: IMeta = {
                count: result.users!.length,
                limit: config.limit || 10,
                page: config.page || 0,
                total,
              };

              return UserActions.loadUsersNextSuccess({
                data: result.users || [],
                meta,
              });
            }),
            catchError((error) =>
              of(
                UserActions.loadUsersNextFailure({ error }),
                globalFailure({ error })
              )
            )
          );
      })
    )
  );

  createUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.createUser),
      filter((action) => action.fromBatch !== true),
      concatMap((_action) => {
        return this.api
          .post<{ object_uuid: string }>('/users', _action.data)
          .pipe(
            map((result) => {
              return UserActions.createUserSuccess({
                request_id: '-1',
                user_uuid: result.object_uuid,
              });
            }),
            catchError((error) => {
              return of(
                UserActions.createUserFailure({ error }),
                globalFailure({ error })
              );
            })
          );
      })
    )
  );

  patchUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.patchUser),
      filter((action) => action.fromBatch !== true),
      concatMap((_action) => {
        return this.api
          .patch<void>('/users/' + _action.user_uuid, _action.data)
          .pipe(
            map(() =>
              UserActions.patchUserSuccess({
                request_id: '-1',
                user_uuid: _action.user_uuid,
                data: _action.data,
              })
            ),
            catchError((error) => {
              return of(
                UserActions.patchUserFailure({
                  error,
                  user_uuid: _action.user_uuid,
                }),
                globalFailure({ error })
              );
            })
          );
      })
    )
  );

  deleteUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.deleteUser),
      filter((action) => action.fromBatch !== true),
      concatMap((_action) => {
        return this.api.delete<void>('/users/' + _action.user_uuid).pipe(
          map(() =>
            UserActions.deleteUserSuccess({
              request_id: '-1',
              user_uuid: _action.user_uuid,
            })
          ),
          catchError((error) => {
            return of(
              UserActions.deleteUserFailure({
                error,
                user_uuid: _action.user_uuid,
              }),
              globalFailure({ error })
            );
          })
        );
      })
    )
  );

  loadUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        UserActions.loadUser,
        UserActions.patchEmailSuccess,
        UserActions.patchUserSuccess,
        UserActions.lockUserSuccess,
        UserActions.changeUserRoleSuccess,
        UserActions.removeUserFromAccountSuccess,
        UserActions.addUserPartnerRoleSuccess,
        UserActions.patchUserPartnerRoleSuccess,
        UserActions.removeUserPartnerRoleSuccess,
        UserActions.toggleFavoriteSuccess
      ),
      filter((action) => action.fromBatch !== true),
      concatMap((_action) => {
        return this.api.get<{ user: User }>('/users/' + _action.user_uuid).pipe(
          map((result) =>
            UserActions.loadUserSuccess({
              data: result.user || {},
              user_uuid: _action.user_uuid,
            })
          ),
          catchError((error) =>
            of(
              UserActions.loadUserFailure({
                error,
                user_uuid: _action.user_uuid,
              }),
              globalFailure({ error, whitelist: [404] })
            )
          )
        );
      })
    )
  );

  /**********************************************!
   *  Single Patch Actions
   ***********************************************/

  toggleFavorite$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.toggleFavorite),
      filter((action) => action.fromBatch !== true),
      concatMap((action) => {
        return this.api
          .patch<void>('/users/' + action.user_uuid, {
            favorite: action.favorite,
          })
          .pipe(
            map(() =>
              UserActions.toggleFavoriteSuccess({
                user_uuid: action.user_uuid,
                favorite: action.favorite,
              })
            ),
            catchError((error) =>
              of(
                UserActions.toggleFavoriteFailure({
                  user_uuid: action.user_uuid,
                  error: error,
                }),
                globalFailure({ error })
              )
            )
          );
      })
    )
  );

  patchEmail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.patchEmail),
      concatMap((action) => {
        return this.api
          .patch<void>('/users/' + action.user_uuid, { email: action.email })
          .pipe(
            map(() => {
              return UserActions.patchEmailSuccess({
                user_uuid: action.user_uuid,
              });
            }),
            catchError((error) =>
              of(
                UserActions.patchEmailFailure({
                  user_uuid: action.user_uuid,
                  error: error,
                })
              )
            )
          );
      })
    )
  );

  lockUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.lockUser),
      concatMap((action) => {
        return this.api
          .patch<User[]>('/users/' + action.user_uuid, {
            locked: action.locked,
          })
          .pipe(
            map((result) => {
              return UserActions.lockUserSuccess({
                user_uuid: action.user_uuid,
              });
            }),
            catchError((error) =>
              of(
                UserActions.lockUserFailure({
                  user_uuid: action.user_uuid,
                  error: error,
                })
              )
            )
          );
      })
    )
  );

  changeUserRole$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.changeUserRole),
      withLatestFrom(this.store.select(getAuth)),
      concatMap(([action, session]) => {
        return this.api
          .patch<User[]>(
            '/partners/' +
              session!.partner_uuid +
              '/contracts/' +
              action.account_uuid +
              '/users/' +
              action.user_uuid,
            { user_role: action.user_role }
          )
          .pipe(
            map((result) => {
              return UserActions.changeUserRoleSuccess({
                user_uuid: action.user_uuid,
                account_uuid: action.account_uuid,
              });
            }),
            catchError((error) =>
              of(
                UserActions.changeUserRoleFailure({
                  user_uuid: action.user_uuid,
                  error,
                })
              )
            )
          );
      })
    )
  );

  removeUserFromAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.removeUserFromAccount),
      withLatestFrom(this.store.select(getAuth)),
      concatMap(([action, session]) => {
        return this.api
          .delete<void>(
            '/partners/' +
              session!.partner_uuid +
              '/contracts/' +
              action.account_uuid +
              '/users/' +
              action.user_uuid
          )
          .pipe(
            map(() => {
              return UserActions.removeUserFromAccountSuccess({
                user_uuid: action.user_uuid,
                account_uuid: action.account_uuid,
              });
            }),
            catchError((error) =>
              of(
                UserActions.removeUserFromAccountFailure({
                  user_uuid: action.user_uuid,
                  error,
                })
              )
            )
          );
      })
    )
  );

  /**********************************************!
   *  User Partner Role
   ***********************************************/

  addUserPartnerRole$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.addUserPartnerRole),
      withLatestFrom(this.store.select(getAuth)),
      concatMap(([action, session]) => {
        return this.api
          .post<void>('/partners/' + session!.partner_uuid + '/users', {
            user_uuid: action.user_uuid,
            user_role: action.user_role,
          })
          .pipe(
            map(() => {
              return UserActions.addUserPartnerRoleSuccess({
                user_uuid: action.user_uuid,
              });
            }),
            catchError((error) =>
              of(
                UserActions.addUserPartnerRoleFailure({
                  user_uuid: action.user_uuid,
                  error: error,
                })
              )
            )
          );
      })
    )
  );

  patchUserPartnerRole$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.patchUserPartnerRole),
      withLatestFrom(this.store.select(getAuth)),
      concatMap(([action, session]) => {
        return this.api
          .patch<void>(
            '/partners/' + session!.partner_uuid + '/users/' + action.user_uuid,
            { user_role: action.user_role }
          )
          .pipe(
            map(() => {
              return UserActions.patchUserPartnerRoleSuccess({
                user_uuid: action.user_uuid,
              });
            }),
            catchError((error) =>
              of(
                UserActions.patchUserPartnerRoleFailure({
                  user_uuid: action.user_uuid,
                  error: error,
                })
              )
            )
          );
      })
    )
  );

  removeUserPartnerRole$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.removeUserPartnerRole),
      withLatestFrom(this.store.select(getAuth)),
      concatMap(([action, session]) => {
        return this.api
          .delete<void>(
            '/partners/' + session!.partner_uuid + '/users/' + action.user_uuid
          )
          .pipe(
            map((result) => {
              return UserActions.removeUserPartnerRoleSuccess({
                user_uuid: action.user_uuid,
              });
            }),
            catchError((error) =>
              of(
                UserActions.removeUserPartnerRoleFailure({
                  user_uuid: action.user_uuid,
                  error: error,
                })
              )
            )
          );
      })
    )
  );
}
